import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { generateNullHandlingSql } from '../utils/sqlGenerator';

interface NullHandlingBuilderProps {
  columnName: string;
  onSqlGenerate: (sql: string) => void;
}

const NULL_FUNCTIONS = [
  {
    value: 'COALESCE',
    label: 'COALESCE - Return first non-NULL value',
    description: 'Replace NULL with a default value',
    example: "COALESCE(phone, 'Not Provided') → 'Not Provided' if phone is NULL"
  },
  {
    value: 'IFNULL',
    label: 'IFNULL - Replace NULL with default',
    description: 'Same as COALESCE but only takes 2 arguments',
    example: "IFNULL(email, 'no-email@example.com')"
  },
  {
    value: 'NULLIF',
    label: 'NULLIF - Return NULL if values match',
    description: 'Convert specific values to NULL',
    example: "NULLIF(status, '') → NULL if status is empty string"
  },
];

export const NullHandlingBuilder: React.FC<NullHandlingBuilderProps> = ({
  columnName,
  onSqlGenerate,
}) => {
  const [selectedFunction, setSelectedFunction] = useState('COALESCE');
  const [defaultValue, setDefaultValue] = useState('');

  const currentFunction = NULL_FUNCTIONS.find(f => f.value === selectedFunction);

  useEffect(() => {
    if (defaultValue) {
      const sql = generateNullHandlingSql(columnName, selectedFunction, defaultValue);
      onSqlGenerate(sql);
    }
  }, [selectedFunction, defaultValue, columnName, onSqlGenerate]);

  return (
    <div className="space-y-4">
      <div>
        <Label>NULL Handling Function</Label>
        <Select value={selectedFunction} onValueChange={setSelectedFunction}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {NULL_FUNCTIONS.map((func) => (
              <SelectItem key={func.value} value={func.value}>
                {func.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>
          {selectedFunction === 'NULLIF' ? 'Value to Compare' : 'Default Value'}
        </Label>
        <Input
          placeholder={
            selectedFunction === 'NULLIF'
              ? "Value to convert to NULL (e.g., '' or 0)"
              : "Default value if NULL (e.g., 'Unknown' or 0)"
          }
          value={defaultValue}
          onChange={(e) => setDefaultValue(e.target.value)}
        />
        <div className="text-xs text-muted-foreground mt-1">
          {selectedFunction === 'NULLIF'
            ? 'If column equals this value, return NULL'
            : 'Value to use when column is NULL'
          }
        </div>
      </div>

      {currentFunction && (
        <div className="text-sm text-muted-foreground">
          <strong>Description:</strong>
          <div className="mt-1">{currentFunction.description}</div>
          <div className="mt-2">
            <strong>Example:</strong>
            <div className="mt-1">{currentFunction.example}</div>
          </div>
        </div>
      )}

      <div className="bg-muted p-3 rounded-md text-sm">
        <strong>Common Use Cases:</strong>
        <ul className="list-disc list-inside mt-1 space-y-1">
          <li>COALESCE: Provide default values for missing data</li>
          <li>NULLIF: Convert empty strings or zeros to NULL</li>
          <li>IFNULL: Simple NULL replacement (same as COALESCE)</li>
        </ul>
      </div>
    </div>
  );
};
